// Restores the chat scroll position when the user returns to the LLM chat from
// the booking review/traveler flow ("back to search result" / logo "Yes leave"
// / browser Back). The user clicks an in-chat flight/hotel result card → goes
// into booking → comes back, and should land on the SAME card they picked, not
// at the top of the chat.
//
// Why a DOM-anchor (not a pixel Y): the chat panel REMOUNTS on return (mobile
// = /chat route remount; desktop = ChatArea remount when DesktopLandingPage
// stops hiding the chat column at ReviewNew), so the fresh scroll container is
// at 0. We reuse the already-instrumented bookable-card anchors
// (data-flight-anchor / data-hotel-anchor) via the proven scrollToBookable*
// helpers — scrollIntoView walks up to the real scroll ancestor itself, so the
// same code works for mobile body-scroll AND the desktop inner overflow:auto
// container. Anchoring on the element (not a fixed Y) also survives the late
// image-decode reflow that shifts card positions after mount.

import { scrollToBookableFlight } from "./flightAnchor";
import { scrollToBookableHotel } from "./hotelAnchor";

const KEY = "_zenvoya_chat_return_anchor";
// The itinerary-details round-trip uses its own scroll key and (on mobile) also
// remounts TopDestination. When that restore is in flight we defer to it so the
// two don't fight. Mirror of the consumer in TopDestination.tsx.
const ITINERARY_SCROLL_KEY = "_zenvoya_top_dest_scroll";
const ITINERARY_SCROLL_TTL_MS = 60000;
// Generous enough for a normal booking round-trip (traveler form + payment can
// take minutes), short enough that a stale entry can't resurface much later.
const TTL_MS = 15 * 60 * 1000;

type ReturnAnchorType = "flight" | "hotel";
interface ReturnAnchor {
  type: ReturnAnchorType;
  id: string;
  ts: number;
}

// Single key (not one per type) so flight-vs-hotel can't both be set at once —
// the last card the user selected always wins. id = flight uniqueId, or the raw
// hotel name (scrollToBookableHotel re-keys it via hotelAnchorKey internally).
export function saveChatReturnAnchor(type: ReturnAnchorType, id?: string | null): void {
  try {
    const payload: ReturnAnchor = { type, id: (id ?? "").toString(), ts: Date.now() };
    sessionStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    /* private mode / quota — silent */
  }
}

export function clearChatReturnAnchor(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* private mode / quota — silent */
  }
}

export function willRestoreChatScroll(isMobile: boolean): boolean {
  try {
    const itinerary = sessionStorage.getItem(ITINERARY_SCROLL_KEY);
    if (itinerary) {
      if (!isMobile) return false;
      const { y, ts } = JSON.parse(itinerary);
      return Number.isFinite(y) && y > 0 && !(Date.now() - ts > ITINERARY_SCROLL_TTL_MS);
    }
    const anchor = JSON.parse(sessionStorage.getItem(KEY) || "null");
    return (
      !!anchor &&
      (anchor.type === "flight" || anchor.type === "hotel") &&
      Number.isFinite(anchor.ts) &&
      !(Date.now() - anchor.ts > TTL_MS)
    );
  } catch {
    return false;
  }
}

// Read + REMOVE the saved anchor (consume-on-read, so it can fire at most once),
// then scroll the matching bookable card into view. No-op when the key is
// absent / stale / unparseable, or when the itinerary-details restore is active
// (that one takes precedence). Re-applies through the image-decode settle window
// so late-loading card images (hotels carry 30-45) don't leave the card
// off-position. Instant ("auto") scroll so the re-applies don't visibly animate.
export function consumeChatReturnAnchorScroll(): void {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(KEY);
    // Defer to the itinerary-details restore (e.g. user went chat → itinerary
    // card → details → back; on mobile that remounts TopDestination too). Drop
    // our (now-stale) anchor without scrolling so the two restores don't fight.
    if (raw && sessionStorage.getItem(ITINERARY_SCROLL_KEY)) {
      sessionStorage.removeItem(KEY);
      return;
    }
    if (raw) sessionStorage.removeItem(KEY);
  } catch {
    return;
  }
  if (!raw) return;

  let data: ReturnAnchor;
  try {
    data = JSON.parse(raw);
  } catch {
    return;
  }
  if (!data || (data.type !== "flight" && data.type !== "hotel")) return;
  if (!Number.isFinite(data.ts) || Date.now() - data.ts > TTL_MS) return;

  const apply = () => {
    if (data.type === "flight") {
      scrollToBookableFlight(data.id, { behavior: "auto", sectionFallback: false });
    } else {
      scrollToBookableHotel(data.id, { behavior: "auto", sectionFallback: false });
    }
  };

  // Wait one frame for the chat list to lay out, then re-apply across the
  // image-decode settle window (mirrors the rAF + 80/240ms pattern in
  // TopDestination.tsx's _zenvoya_top_dest_scroll restore, extended for the
  // image-heavy hotel lists).
  requestAnimationFrame(() => {
    apply();
    setTimeout(apply, 150);
    setTimeout(apply, 400);
    setTimeout(apply, 800);
  });
}
